import { useId, useState } from 'react'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { useT } from '../../i18n/useI18n'
import { AccountApiError, EMAIL_MAX_LENGTH } from '../../account/accountApi'
import { useAccountRequest } from '../../account/useAccountRequest'

export interface TextFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  type: 'email' | 'password' | 'text'
  autoComplete: string
  required: boolean
  minLength?: number
  maxLength: number
  hint?: string
}

export function TextField({ label, value, onChange, type, autoComplete, required, minLength, maxLength, hint }: TextFieldProps): ReactElement {
  const t = useT()
  const id: string = useId()
  return (
    <div className="ac-field">
      <label className="form-label" htmlFor={id}>{label}{!required && <span className="ac-optional"> ({t('account.optional')})</span>}</label>
      <input id={id} className="form-control" type={type} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} required={required} minLength={minLength} maxLength={maxLength} aria-describedby={hint !== undefined ? `${id}-hint` : undefined} />
      {hint !== undefined && <div id={`${id}-hint`} className="form-text">{hint}</div>}
    </div>
  )
}

export function SubmitButton({ label, sending }: { label: string; sending: boolean }): ReactElement {
  const t = useT()
  return (
    <button type="submit" className="btn btn-primary ac-submit" disabled={sending}>
      {sending ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> {t('account.sending')}</> : label}
    </button>
  )
}

/** A service rejection shows the service's own message; a network or rate-limit failure gets a plain explanation plus the technical detail. */
export function AccountError({ error }: { error: Error }): ReactElement {
  const t = useT()
  const explained: boolean = error instanceof AccountApiError && error.kind !== 'rejected'
  return (
    <div className="ac-alert" role="alert">
      <i className="bi bi-exclamation-triangle" aria-hidden="true" />
      <div>
        {explained && <p className="ac-alert-text">{t(`account.error.${(error as AccountApiError).kind}`)}</p>}
        <p className={explained ? 'ac-alert-detail' : 'ac-alert-text'}>{error.message}</p>
      </div>
    </div>
  )
}

export function AccountNotice({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <div className="ac-notice" role="status">
      <i className="bi bi-check2-circle" aria-hidden="true" />
      <div>
        <p className="ac-notice-title">{title}</p>
        {children}
      </div>
    </div>
  )
}

/** One e-mail field and a submit button; once the call succeeds, the form gives way to `renderDone`. */
export function EmailRequestForm({ submitLabel, send, renderDone }: { submitLabel: string; send: (email: string) => Promise<void>; renderDone: (email: string) => ReactElement }): ReactElement {
  const t = useT()
  const [email, setEmail] = useState<string>('')
  const { state, run } = useAccountRequest()
  if (state.status === 'done') return renderDone(email.trim())
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    void run(() => send(email))
  }
  return (
    <form className="ac-form" onSubmit={submit}>
      <TextField label={t('account.email')} value={email} onChange={setEmail} type="email" autoComplete="email" required maxLength={EMAIL_MAX_LENGTH} />
      {state.status === 'failed' && <AccountError error={state.error} />}
      <SubmitButton label={submitLabel} sending={state.status === 'sending'} />
    </form>
  )
}
